// SplitPane — the split-view SECONDARY pane (CFR-VW-145). Renders a second app beside the primary
// (route) app at the SAME scope node, through the SAME <GuardedApp> guards (no authz bypass).
// Same-scope only (apps read scope from the route via useScopeData); cross-scope would need a
// per-app scope-via-props refactor. Session-only (not deep-linkable) by decision. Renders nothing
// when not split. getSubApp gives the header title; GuardedApp still runs the real guard chain.
import { useEffect } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";
import { canonicalSubAppId, getSubApp, ZARVIS_APP_ID } from "@perlab/registry";
import { useMountScope } from "../state/useMountScope";
import { GuardedApp } from "./GuardedApp";
import { AppContentPanel } from "./AppContentPanel";

export function canUseSplitView(
  primaryAppId: string | undefined,
  secondaryAppId: string | undefined,
): boolean {
  if (!secondaryAppId) return false;
  return canonicalSubAppId(secondaryAppId) !== ZARVIS_APP_ID
    && (!primaryAppId || canonicalSubAppId(primaryAppId) !== ZARVIS_APP_ID);
}

export function SplitPane(): React.ReactElement | null {
  const navigate = useNavigate();
  const { app: primaryAppId } = useParams({ strict: false }) as { app?: string };
  const { split: splitAppId } = useSearch({ strict: false }) as { split?: string };
  const closeSplit = (): void => {
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, split: undefined }) } as never);
  };
  const { currentLevel, levelPending, levelError } = useMountScope();
  const splitAllowed = canUseSplitView(primaryAppId, splitAppId);

  useEffect(() => {
    if (!splitAppId || splitAllowed) return;
    navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, split: undefined }),
    } as never);
  }, [navigate, splitAllowed, splitAppId]);

  if (!splitAppId || !splitAllowed) return null;
  const name = getSubApp(splitAppId)?.name ?? splitAppId;

  return (
    <div
      data-app="split-pane"
      className="flex min-w-0 flex-1 flex-col border-l border-[var(--color-border-subtle)]"
    >
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-[var(--color-border-default)] bg-[var(--surface-sunken)] px-3 py-1">
        <span className="truncate text-[13px] font-medium text-[var(--color-text-primary)]">{name}</span>
        <button
          type="button"
          aria-label="Close split view"
          title="Close split view"
          onClick={closeSplit}
          className="ml-auto rounded-[2px] p-0.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        >
          <X size={14} aria-hidden />
        </button>
      </div>
      <AppContentPanel>
        <GuardedApp appId={splitAppId} currentLevel={currentLevel} levelPending={levelPending} levelError={levelError} />
      </AppContentPanel>
    </div>
  );
}
